import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob, apiMultipart } from '../../lib/api.ts';
import { Download, ImageDown, Layers, Search, Upload } from 'lucide-react';
import { Aviso, Boton, BotonArchivo, Campo, Casilla, Chip, EncabezadoPagina, Selector, Tarjeta } from '../../componentes/ui.tsx';
import { ModalImportar } from '../../componentes/Importar.tsx';

export interface ProductoAdmin {
  id: string;
  referencia: string;
  nombre: string;
  marcaId: string;
  marcaNombre: string;
  categoria: string;
  tipo: string | null;
  coleccion: string | null;
  linea: string | null;
  familia: string | null;
  color: string | null;
  pvpSugerido: number | null;
  imagenUrl: string | null;
  tieneImagen: boolean;
  fechaExpiracion: string | null;
  estado: string;
}

interface Marca {
  id: string;
  nombre: string;
}

export function PaginaAdminProductos() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [soloSinImagen, setSoloSinImagen] = useState(false);
  const [editando, setEditando] = useState<ProductoAdmin | null>(null);
  const [importando, setImportando] = useState(false);
  const [buscandoFotos, setBuscandoFotos] = useState(false);
  const [editandoMasivo, setEditandoMasivo] = useState(false);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const productos = useQuery({
    queryKey: ['admin-productos', q, soloSinImagen],
    queryFn: () =>
      api<{ items: ProductoAdmin[]; siguienteCursor: string | null }>(
        `/admin/productos?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}${soloSinImagen ? '&sinImagen=true' : ''}`,
      ),
  });
  const marcas = useQuery({
    queryKey: ['admin-marcas'],
    queryFn: () => api<{ items: Marca[] }>('/admin/marcas'),
  });

  async function descargarInformeSinFoto() {
    const blob = await apiBlob('/admin/productos/sin-foto.xlsx');
    if (!blob) {
      setError('No se pudo generar el informe.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'productos-sin-foto.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  // Mismo formato del importador: editar el archivo y re-importarlo actualiza el catálogo.
  async function descargarExcel() {
    const blob = await apiBlob('/admin/productos.xlsx');
    if (!blob) {
      setError('No se pudo generar el Excel.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'productos.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo="Productos"
        subtitulo={`${productos.data?.items.length ?? 0} en esta vista`}
        acciones={
          <>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-suave" />
              <input
                placeholder="Buscar por código o nombre"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="campo con-icono w-64 bg-superficie shadow-[var(--sombra)]"
              />
            </div>
            <Chip activo={soloSinImagen} onClick={() => setSoloSinImagen((v) => !v)}>
              Sin imagen
            </Chip>
            {soloSinImagen ? (
              <Boton variante="contorno" onClick={() => void descargarInformeSinFoto()}>
                <Download size={16} /> Informe
              </Boton>
            ) : null}
            <Boton variante="contorno" onClick={() => setImportando(true)}>
              <Upload size={16} /> Importar
            </Boton>
            <Boton variante="contorno" onClick={() => void descargarExcel()}>
              <Download size={16} /> Excel
            </Boton>
            <Boton variante="contorno" onClick={() => setBuscandoFotos(true)}>
              <ImageDown size={16} /> Buscar fotos
            </Boton>
            <Boton variante="contorno" onClick={() => setEditandoMasivo(true)}>
              <Layers size={16} /> Edición masiva{marcados.size > 0 ? ` (${marcados.size})` : ''}
            </Boton>
          </>
        }
      />

      {soloSinImagen ? (
        <p className="text-sm text-suave">
          Estos productos no aparecen a los clientes hasta tener imagen (regla D41).
        </p>
      ) : null}
      {error ? (
        <Aviso tono={error.startsWith('__ok__') ? 'ok' : 'error'}>{error.replace('__ok__', '')}</Aviso>
      ) : null}

      <Tarjeta className="overflow-x-auto !p-2">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-suave">
            <tr>
              <th className="px-3 py-3">
                <Casilla
                  activa={(productos.data?.items.length ?? 0) > 0 && productos.data!.items.every((p) => marcados.has(p.id))}
                  onCambiar={(v) =>
                    setMarcados(v ? new Set(productos.data?.items.map((p) => p.id) ?? []) : new Set())
                  }
                />
              </th>
              <th className="px-4 py-3">Imagen</th>
              <th className="px-4 py-3">Referencia</th>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Marca</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Expira</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-superficie2">
            {productos.data?.items.map((p) => (
              <tr key={p.id} className="rounded-2xl hover:bg-superficie2">
                <td className="px-3 py-2">
                  <Casilla
                    activa={marcados.has(p.id)}
                    onCambiar={(v) =>
                      setMarcados((previa) => {
                        const nueva = new Set(previa);
                        if (v) nueva.add(p.id);
                        else nueva.delete(p.id);
                        return nueva;
                      })
                    }
                  />
                </td>
                <td className="px-4 py-2">
                  {p.imagenUrl ? (
                    <img src={p.imagenUrl} alt="" className="h-11 w-11 rounded-full bg-superficie2 object-contain" />
                  ) : (
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-rose-500/10 text-[10px] font-medium text-rose-500">
                      sin
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{p.referencia}</td>
                <td className="px-4 py-2">{p.nombre}</td>
                <td className="px-4 py-2">{p.marcaNombre}</td>
                <td className="px-4 py-2">{p.categoria}</td>
                <td className="px-4 py-2 text-xs text-suave">
                  {p.fechaExpiracion ? p.fechaExpiracion.slice(0, 10) : '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  <Boton variante="contorno" tamano="sm" onClick={() => setEditando(p)}>
                    Editar
                  </Boton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {productos.data?.items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-suave">Sin productos. Use Importar.</p>
        ) : null}
      </Tarjeta>

      {editandoMasivo ? (
        <ModalEdicionMasiva
          idsSeleccionados={[...marcados]}
          marcas={marcas.data?.items ?? []}
          alCerrar={() => setEditandoMasivo(false)}
          alListo={async (n) => {
            setEditandoMasivo(false);
            setMarcados(new Set());
            setError(null);
            await qc.invalidateQueries({ queryKey: ['admin-productos'] });
            setError(`__ok__${n} producto(s) actualizados.`);
          }}
        />
      ) : null}

      {buscandoFotos ? (
        <ModalBuscarFotos
          alCerrar={() => setBuscandoFotos(false)}
          alListo={async () => {
            await qc.invalidateQueries({ queryKey: ['admin-productos'] });
          }}
        />
      ) : null}

      {importando ? (
        <ModalImportar
          tipo="PRODUCTOS"
          titulo="Importar productos"
          columnas="Acepta el Excel de la empresa tal cual (Codigo, Linea, Descripcion Fabrica, Categoría de producto, Marca, Familia, Coleccion; las tallas repetidas se importan una sola vez) o las columnas codigo, marca, nombre, categoria, tipo, coleccion, linea, familia, fecha_expiracion. Fotos: en el ZIP nombradas codigo.png, o después con Buscar fotos."
          conZip
          alCerrar={() => setImportando(false)}
          alAplicado={async () => {
            setImportando(false);
            await qc.invalidateQueries({ queryKey: ['admin-productos'] });
          }}
        />
      ) : null}

      {editando ? (
        <ModalEditarProducto
          producto={editando}
          marcas={marcas.data?.items ?? []}
          alCerrar={() => setEditando(null)}
          alGuardar={async () => {
            setEditando(null);
            setError(null);
            await qc.invalidateQueries({ queryKey: ['admin-productos'] });
          }}
          alFallar={setError}
        />
      ) : null}
    </main>
  );
}

/** Corrección de un producto mal importado: datos y foto (se normaliza cuadrada, D21). */
export function ModalEditarProducto({
  producto,
  marcas,
  alCerrar,
  alGuardar,
  alFallar,
}: {
  producto: ProductoAdmin;
  marcas: Marca[];
  alCerrar: () => void;
  alGuardar: () => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [nombre, setNombre] = useState(producto.nombre);
  const [marcaId, setMarcaId] = useState(producto.marcaId);
  const [categoria, setCategoria] = useState(producto.categoria);
  const [tipo, setTipo] = useState(producto.tipo ?? '');
  const [coleccion, setColeccion] = useState(producto.coleccion ?? '');
  const [linea, setLinea] = useState(producto.linea ?? '');
  const [familia, setFamilia] = useState(producto.familia ?? '');
  const [color, setColor] = useState(producto.color ?? '');
  const [expira, setExpira] = useState(producto.fechaExpiracion?.slice(0, 10) ?? '');
  const [foto, setFoto] = useState<File | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  function elegirFoto(archivo: File | null) {
    setFoto(archivo);
    setPrevia(archivo ? URL.createObjectURL(archivo) : null);
  }
  const [guardando, setGuardando] = useState(false);

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    setGuardando(true);
    try {
      await api(`/admin/productos/${producto.id}`, {
        metodo: 'PATCH',
        cuerpo: {
          nombre: nombre.trim(),
          marcaId,
          categoria: categoria.trim(),
          tipo: tipo.trim() || undefined,
          coleccion: coleccion.trim() || undefined,
          linea: linea.trim() || undefined,
          familia: familia.trim() || undefined,
          color: color.trim() || undefined,
          ...(expira
            ? { fechaExpiracion: new Date(`${expira}T23:59:59`).toISOString() }
            : { fechaExpiracion: undefined }),
        },
      });
      if (foto) {
        const forma = new FormData();
        forma.append('archivo', foto);
        await apiMultipart(`/admin/productos/${producto.id}/imagen`, forma);
      }
      await alGuardar();
    } catch (e) {
      alFallar((e as Error).message);
      alCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={guardar}
        className="tarjeta max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto p-6 shadow-[var(--sombra-alta)]"
      >
        <h2 className="text-[22px] font-light">
          Editar <span className="font-medium">{producto.referencia}</span>
        </h2>

        <div className="flex items-center gap-3">
          <img
            src={previa ?? producto.imagenUrl ?? ''}
            alt=""
            className="interno h-20 w-20 object-contain p-1"
          />
          <div className="flex-1">
            <p className="mb-1.5 text-[13px] text-suave">
              {producto.tieneImagen ? 'Reemplazar foto' : 'Subir foto (hoy es invisible, D41)'}
            </p>
            <BotonArchivo etiqueta="Elegir imagen" accept="image/*" onArchivo={elegirFoto} nombre={foto?.name ?? null} />
          </div>
        </div>

        <Campo etiqueta="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Selector etiqueta="Marca" value={marcaId} onChange={(e) => setMarcaId(e.target.value)}>
            {marcas.map((m) => (
              <option key={m.id} value={m.id}>{m.nombre}</option>
            ))}
          </Selector>
          <Campo etiqueta="Categoría" value={categoria} onChange={(e) => setCategoria(e.target.value)} required />
          <Campo etiqueta="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} />
          <Campo etiqueta="Colección" value={coleccion} onChange={(e) => setColeccion(e.target.value)} />
          <Campo etiqueta="Línea" value={linea} onChange={(e) => setLinea(e.target.value)} />
          <Campo etiqueta="Familia" value={familia} onChange={(e) => setFamilia(e.target.value)} />
          <Campo etiqueta="Color" value={color} onChange={(e) => setColor(e.target.value)} />
          <Campo etiqueta="Expira (vacío = nunca)" type="date" value={expira} onChange={(e) => setExpira(e.target.value)} />
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <Boton type="button" variante="peligro" onClick={() => setConfirmandoBorrado(true)}>
            Borrar
          </Boton>
          <div className="flex gap-2">
            <Boton type="button" variante="secundario" onClick={alCerrar}>Cancelar</Boton>
            <Boton type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Boton>
          </div>
        </div>

        {confirmandoBorrado ? (
          <ConfirmarBorrado
            mensaje={`Se borra ${producto.referencia} definitivamente. Los almacenes dejan de verlo; el historial de pedidos conserva su snapshot.`}
            alCancelar={() => setConfirmandoBorrado(false)}
            alConfirmar={async () => {
              try {
                await api(`/admin/productos/${producto.id}`, { metodo: 'DELETE' });
                await alGuardar();
              } catch (e) {
                alFallar((e as Error).message);
                alCerrar();
              }
            }}
          />
        ) : null}
      </form>
    </div>
  );
}


/** Enlaza fotos desde el servidor de la empresa: urlBase + MARCA/codigo.png (sin ocupar espacio). */
function ModalBuscarFotos({ alCerrar, alListo }: { alCerrar: () => void; alListo: () => Promise<void> }) {
  const [urlBase, setUrlBase] = useState('');
  const [resultado, setResultado] = useState<{ revisados: number; encontradas: number; sinFoto: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  const config = useQuery({
    queryKey: ['config-fotos'],
    queryFn: () => api<{ urlBase: string | null }>('/admin/config-fotos'),
  });
  useEffect(() => {
    if (config.data) setUrlBase(config.data.urlBase ?? '');
  }, [config.data]);

  async function buscar() {
    setBuscando(true);
    setError(null);
    try {
      await api('/admin/config-fotos', { metodo: 'PATCH', cuerpo: { urlBase: urlBase.trim() || null } });
      const r = await api<{ revisados: number; encontradas: number; sinFoto: string[] }>(
        '/admin/productos/buscar-fotos',
        { metodo: 'POST', cuerpo: {} },
      );
      setResultado(r);
      await alListo();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBuscando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">Buscar fotos</h2>
        <p className="text-sm text-suave">
          Busca la foto de cada producto sin imagen en el servidor de la organización y la enlaza
          (no ocupa espacio aquí). La estructura es: URL base + MARCA en mayúsculas (espacios como _) + /codigo.png
        </p>
        <Campo
          etiqueta="URL base de las fotos"
          value={urlBase}
          onChange={(e) => setUrlBase(e.target.value)}
          placeholder="https://imagenes-erp.suempresa.com/img2/"
          type="url"
        />
        {urlBase.trim() ? (
          <p className="break-all font-mono text-[11px] text-suave">
            Ej.: {`${urlBase.trim().replace(/\/$/, '')}/KLIN/178158-2.KN.png`}
          </p>
        ) : null}
        {resultado ? (
          <Aviso tono={resultado.encontradas > 0 ? 'ok' : 'error'}>
            {resultado.encontradas} foto(s) enlazada(s) de {resultado.revisados} producto(s) revisados.
            {resultado.sinFoto.length > 0 ? ` Sin foto: ${resultado.sinFoto.join(', ')}${resultado.revisados - resultado.encontradas > resultado.sinFoto.length ? '…' : ''}` : ''}
          </Aviso>
        ) : null}
        {error ? <Aviso>{error}</Aviso> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>{resultado ? 'Cerrar' : 'Cancelar'}</Boton>
          <Boton type="button" disabled={buscando || !urlBase.trim()} onClick={() => void buscar()}>
            {buscando ? 'Buscando…' : 'Buscar fotos'}
          </Boton>
        </div>
      </div>
    </div>
  );
}


/** Edición masiva: alcance por selección manual o por filtro; cambia tipo, colección, línea y caducidad. */
function ModalEdicionMasiva({
  idsSeleccionados,
  marcas,
  alCerrar,
  alListo,
}: {
  idsSeleccionados: string[];
  marcas: Marca[];
  alCerrar: () => void;
  alListo: (afectados: number) => Promise<void>;
}) {
  const [alcance, setAlcance] = useState<'seleccion' | 'filtro'>(idsSeleccionados.length > 0 ? 'seleccion' : 'filtro');
  const [marcaId, setMarcaId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [linea, setLinea] = useState('');
  const [coleccion, setColeccion] = useState('');
  const [familia, setFamilia] = useState('');

  const [asignarClienteId, setAsignarClienteId] = useState('');
  const [costo, setCosto] = useState('');
  const [nuevoTipo, setNuevoTipo] = useState('');
  const [nuevaColeccion, setNuevaColeccion] = useState('');
  const [nuevaLinea, setNuevaLinea] = useState('');
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [quitarFecha, setQuitarFecha] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [afectados, setAfectados] = useState<number | null>(null);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);

  const clientes = useQuery({
    queryKey: ['admin-clientes-lista'],
    queryFn: () => api<{ items: { id: string; razonSocial: string; nombreNegocio: string | null }[] }>('/admin/clientes?limit=100'),
  });
  const facetas = useQuery({
    queryKey: ['admin-productos-facetas'],
    queryFn: () =>
      api<{ categorias: string[]; lineas: string[]; colecciones: string[]; familias: string[] }>(
        '/admin/productos/facetas',
      ),
  });

  const seleccion =
    alcance === 'seleccion'
      ? { ids: idsSeleccionados }
      : {
          filtros: {
            ...(marcaId ? { marcaId } : {}),
            ...(categoria ? { categoria } : {}),
            ...(linea ? { linea } : {}),
            ...(coleccion ? { coleccion } : {}),
            ...(familia ? { familia } : {}),
          },
        };
  const hayAlcance = alcance === 'seleccion' ? idsSeleccionados.length > 0 : Object.keys(seleccion.filtros ?? {}).length > 0;
  const costoNumero = costo ? Math.round(Number(costo.replace(',', '.')) * 100) / 100 : null;
  const asignacionValida = Boolean(asignarClienteId && costoNumero !== null && Number.isFinite(costoNumero));
  const hayCambios =
    Boolean(nuevoTipo.trim() || nuevaColeccion.trim() || nuevaLinea.trim() || nuevaFecha || quitarFecha) ||
    asignacionValida;

  // El contador se recalcula al cambiar el alcance (previsualización sin escribir).
  useEffect(() => {
    setAfectados(null);
    if (!hayAlcance) return;
    let cancelado = false;
    void api<{ afectados: number }>('/admin/productos/edicion-masiva', {
      metodo: 'POST',
      cuerpo: { previsualizar: true, seleccion, cambios: {} },
    }).then((r) => {
      if (!cancelado) setAfectados(r.afectados);
    }).catch(() => undefined);
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se recalcula por los valores del alcance
  }, [alcance, marcaId, categoria, linea, coleccion, familia, idsSeleccionados.length]);

  async function aplicar() {
    setGuardando(true);
    setError(null);
    try {
      const r = await api<{ afectados: number }>('/admin/productos/edicion-masiva', {
        metodo: 'POST',
        cuerpo: {
          seleccion,
          cambios: {
            ...(nuevoTipo.trim() ? { tipo: nuevoTipo.trim() } : {}),
            ...(nuevaColeccion.trim() ? { coleccion: nuevaColeccion.trim() } : {}),
            ...(nuevaLinea.trim() ? { linea: nuevaLinea.trim() } : {}),
            ...(quitarFecha
              ? { fechaExpiracion: null }
              : nuevaFecha
                ? { fechaExpiracion: new Date(`${nuevaFecha}T23:59:59`).toISOString() }
                : {}),
            ...(asignacionValida ? { asignarClienteId, costoUnitario: costoNumero } : {}),
          },
        },
      });
      await alListo(r.afectados);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGuardando(false);
    }
  }

  const opciones = (valores: string[] | undefined) =>
    (valores ?? []).map((v) => (
      <option key={v} value={v}>
        {v}
      </option>
    ));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">Edición masiva</h2>

        <div className="flex gap-2">
          <button type="button" className="chip" data-activo={alcance === 'seleccion'} onClick={() => setAlcance('seleccion')} disabled={idsSeleccionados.length === 0}>
            Seleccionados · {idsSeleccionados.length}
          </button>
          <button type="button" className="chip" data-activo={alcance === 'filtro'} onClick={() => setAlcance('filtro')}>
            Por filtro
          </button>
        </div>

        {alcance === 'filtro' ? (
          <div className="grid grid-cols-2 gap-3">
            <Selector etiqueta="Marca" value={marcaId} onChange={(e) => setMarcaId(e.target.value)}>
              <option value="">Todas</option>
              {marcas.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </Selector>
            <Selector etiqueta="Categoría" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">Todas</option>
              {opciones(facetas.data?.categorias)}
            </Selector>
            <Selector etiqueta="Línea" value={linea} onChange={(e) => setLinea(e.target.value)}>
              <option value="">Todas</option>
              {opciones(facetas.data?.lineas)}
            </Selector>
            <Selector etiqueta="Colección" value={coleccion} onChange={(e) => setColeccion(e.target.value)}>
              <option value="">Todas</option>
              {opciones(facetas.data?.colecciones)}
            </Selector>
            <Selector etiqueta="Familia" value={familia} onChange={(e) => setFamilia(e.target.value)}>
              <option value="">Todas</option>
              {opciones(facetas.data?.familias)}
            </Selector>
          </div>
        ) : null}

        <p className="text-sm text-suave">
          {hayAlcance
            ? afectados === null
              ? 'Contando productos…'
              : `Se modificarán ${afectados} producto(s).`
            : 'Marque productos en la lista o elija al menos un filtro.'}
        </p>

        <div className="grid grid-cols-2 gap-3 border-t border-superficie2 pt-3">
          <Campo etiqueta="Nuevo tipo" value={nuevoTipo} onChange={(e) => setNuevoTipo(e.target.value)} placeholder="sin cambio" />
          <Campo etiqueta="Nueva colección" value={nuevaColeccion} onChange={(e) => setNuevaColeccion(e.target.value)} placeholder="sin cambio" />
          <Campo etiqueta="Nueva línea" value={nuevaLinea} onChange={(e) => setNuevaLinea(e.target.value)} placeholder="sin cambio" />
          <Campo etiqueta="Nueva caducidad" type="date" value={nuevaFecha} onChange={(e) => setNuevaFecha(e.target.value)} disabled={quitarFecha} />
        </div>
        <Casilla activa={quitarFecha} onCambiar={setQuitarFecha} etiqueta="Quitar la caducidad (no expiran nunca)" />
        <p className="text-xs text-suave">Los campos vacíos no se tocan. Al caducar, un producto se borra de verdad.</p>

        <div className="grid grid-cols-2 gap-3 border-t border-superficie2 pt-3">
          <Selector etiqueta="Asignar a un almacén" value={asignarClienteId} onChange={(e) => setAsignarClienteId(e.target.value)}>
            <option value="">No asignar</option>
            {clientes.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.nombreNegocio ?? c.razonSocial}</option>
            ))}
          </Selector>
          {asignarClienteId ? (
            <Campo
              etiqueta="Costo unitario para el almacén"
              value={costo}
              onChange={(e) => setCosto(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              required
            />
          ) : null}
        </div>
        {asignarClienteId ? (
          <p className="text-xs text-suave">
            El almacén pasa a ver estos productos con ese costo (asignación manual; una compra
            posterior lo actualiza al costo del pedido más reciente).
          </p>
        ) : null}

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          <Boton
            variante="peligro"
            type="button"
            disabled={!hayAlcance || afectados === null || afectados === 0}
            onClick={() => setConfirmandoBorrado(true)}
          >
            Borrar{afectados ? ` ${afectados}` : ''}
          </Boton>
          <div className="flex gap-2">
            <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
            <Boton type="button" disabled={guardando || !hayAlcance || !hayCambios || afectados === 0} onClick={() => void aplicar()}>
              {guardando ? 'Aplicando…' : `Aplicar${afectados ? ` a ${afectados}` : ''}`}
            </Boton>
          </div>
        </div>

        {confirmandoBorrado ? (
          <ConfirmarBorrado
            mensaje={`Se borran ${afectados} producto(s) definitivamente. Los almacenes dejan de verlos; el historial de pedidos conserva sus snapshots.`}
            alCancelar={() => setConfirmandoBorrado(false)}
            alConfirmar={async () => {
              try {
                const r = await api<{ borrados: number }>('/admin/productos/borrado-masivo', {
                  metodo: 'POST',
                  cuerpo: { seleccion },
                });
                await alListo(r.borrados);
              } catch (e) {
                setConfirmandoBorrado(false);
                setError((e as Error).message);
              }
            }}
          />
        ) : null}
      </div>
    </div>
  );
}


/** Confirmación destructiva: el botón queda bloqueado 3 s con cuenta regresiva visible. */
export function ConfirmarBorrado({
  mensaje,
  alCancelar,
  alConfirmar,
}: {
  mensaje: string;
  alCancelar: () => void;
  alConfirmar: () => Promise<void>;
}) {
  const [restante, setRestante] = useState(3);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    if (restante <= 0) return;
    const timer = setInterval(() => setRestante((r) => r - 1), 1000);
    return () => clearInterval(timer);
  }, [restante]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta w-full max-w-md space-y-4 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">¿Borrar definitivamente?</h2>
        <p className="text-sm text-suave">{mensaje} No hay vuelta atrás.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCancelar} disabled={borrando}>
            Cancelar
          </Boton>
          <button
            type="button"
            onClick={() => {
              setBorrando(true);
              void alConfirmar();
            }}
            disabled={restante > 0 || borrando}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-rose-600 px-5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {borrando ? 'Borrando…' : restante > 0 ? `Borrar (${restante})` : 'Borrar definitivamente'}
          </button>
        </div>
      </div>
    </div>
  );
}
